import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * One screen, nothing to scroll (Marek, 2026-09-30: on a small Android phone a game's START and the
 * bottom of its board sat below the fold, and a board keeps its touches for the game, so the page
 * could hardly be scrolled). The content is scaled down — never up — just enough to fit the height
 * left in the scrolling area it sits in, beside whatever else is there. A phone with room keeps it at
 * full size. Below `min` it stops shrinking and the area scrolls as before, rather than playing a
 * game too small to hit.
 *
 * Only the picture is scaled: the content keeps its own layout size, so a board that measures itself
 * (offsetWidth) is unchanged, and one that reads a touch goes through the kit's `localPoint`.
 */
export function FitToScreen({ children, min = 0.6 }: { children: ReactNode; min?: number }) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ scale: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const box = outer.current;
    const content = inner.current;
    if (!box || !content) return;
    const area = scrollingArea(box);
    if (!area) return;
    const read = () => {
      const natural = content.offsetHeight;
      if (natural <= 0 || area.clientHeight <= 0) return;
      const style = getComputedStyle(area);
      const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      // Everything else in the area: what sits above and below this, as laid out now.
      const all = [...area.children].reduce((sum, child) => sum + (child as HTMLElement).offsetHeight, 0);
      const others = all - box.offsetHeight;
      // A few pixels to spare, for margins that fold out of the boxes measured.
      const room = area.clientHeight - pad - others - 4;
      const scale = Math.max(min, Math.min(1, room / natural));
      const next = scale > 0.995 ? null : { scale: Math.round(scale * 1000) / 1000, height: Math.ceil(natural * scale) };
      setFit((now) => (now?.scale === next?.scale && now?.height === next?.height ? now : next));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(read);
    watch.observe(area);
    watch.observe(content);
    for (const child of area.children) watch.observe(child);
    return () => watch.disconnect();
  }, [min]);

  // Both hold their content's margins inside them (flow-root), so what is measured is all of it.
  return (
    <div ref={outer} style={{ display: 'flow-root', ...(fit ? { height: fit.height } : {}) }}>
      <div ref={inner} style={{ display: 'flow-root', ...(fit ? { transform: `scale(${fit.scale})`, transformOrigin: 'top center' } : {}) }}>
        {children}
      </div>
    </div>
  );
}

/** The nearest box above `el` that scrolls up and down: the space a screen has to fit in. */
function scrollingArea(el: HTMLElement): HTMLElement | null {
  for (let at = el.parentElement; at; at = at.parentElement) {
    const y = getComputedStyle(at).overflowY;
    if (y === 'auto' || y === 'scroll') return at;
  }
  return null;
}
